import clsx from 'clsx';
import { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { PiImages } from 'react-icons/pi';
import { FormattedMessage } from 'react-intl';
import { useBoolean } from 'usehooks-ts';
import { FileType, ImageFileSizeRange, ImageMimeExtMap } from '@_/constants';
import { useImageObjectUrl, useTailwindBreakpoint } from '@_/hooks';
import { buttonCommonI18n } from '@_/i18n-common';
import { Button, EmptyState, Image } from '@_/ui';
import { i18nMessages } from '../../shared/i18nMessages.ts';
import { FileSelector } from '../FileSelector/index.ts';

const sizeMap = {
  sm: { className: clsx('h-40'), radius: '5rem' },
  md: { className: clsx('h-60'), radius: '7.5rem' },
  lg: { className: clsx('h-80'), radius: '10rem' }
};

export interface ImageUploaderProps {
  disabled?: boolean;
  guideShape?: 'circle' | 'none';
  onChange?: (file: File | null) => void;
  size?: 'sm' | 'md' | 'lg';
  value: File | string | null;
}

export function ImageUploader({
  disabled = false,
  guideShape = 'none',
  onChange,
  size = 'md',
  value
}: ImageUploaderProps) {
  const isDesktop = useTailwindBreakpoint('sm');

  const imageUrl = useImageObjectUrl({ value });

  const {
    value: isDropping,
    setTrue: setDroppingTrue,
    setFalse: setDroppingFalse
  } = useBoolean(false);

  const handleSelectFile = useCallback(
    (data: { file: File }) => {
      onChange?.(data.file);
    },
    [onChange]
  );

  const handleDrop = useCallback(
    (files: File[]) => {
      onChange?.(files[0]);
      setDroppingFalse();
    },
    [onChange, setDroppingFalse]
  );

  const { getRootProps, getInputProps } = useDropzone({
    accept: ImageMimeExtMap,
    disabled,
    minSize: ImageFileSizeRange.min,
    maxSize: ImageFileSizeRange.max,
    multiple: true,
    noClick: true,
    maxFiles: 1,
    onDragEnter: setDroppingTrue,
    onDragLeave: setDroppingFalse,
    onDrop: handleDrop
  });

  return (
    <>
      {imageUrl ? (
        <>
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-gray-100">
            <div className={clsx('relative', sizeMap[size].className)}>
              <Image
                className="pointer-events-none mx-auto aspect-[3/2] h-full max-w-full"
                fit="cover"
                src={imageUrl}
              />
              {guideShape === 'circle' && (
                <div
                  className="pointer-events-none absolute inset-0 bg-black/75"
                  style={{
                    mask: `radial-gradient(${sizeMap[size].radius}, transparent 100%, black)`
                  }}
                />
              )}
            </div>
            <div className="flex items-center justify-end gap-x-2 bg-white px-2 py-2">
              <Button
                disabled={disabled}
                onClick={() => onChange?.(null)}
                size={isDesktop ? 'xs' : 'sm'}
                variant="outline"
              >
                <FormattedMessage {...buttonCommonI18n.remove} />
              </Button>
              <FileSelector
                disabled={disabled}
                fileType={FileType.Image}
                onSelectFile={handleSelectFile}
                render={<Button size={isDesktop ? 'xs' : 'sm'} variant="outline" />}
              >
                <FormattedMessage {...buttonCommonI18n.update} />
              </FileSelector>
            </div>
          </div>
        </>
      ) : (
        <div {...getRootProps()}>
          <FileSelector
            className={clsx('relative', sizeMap[size].className)}
            disabled={disabled}
            fileType={FileType.Image}
            inputProps={getInputProps()}
            onSelectFile={handleSelectFile}
            render={<div />}
          >
            {isDropping ? (
              <div className="pointer-events-none absolute inset-0 z-0 flex items-center justify-center rounded-lg border-4 border-dashed border-primary-300 text-xl font-semibold text-primary-500">
                <FormattedMessage {...i18nMessages.dropUploadFiles} />
              </div>
            ) : (
              <div className="flex h-full items-center justify-center rounded-lg border-2 border-dashed border-gray-300">
                <EmptyState
                  icon={<PiImages />}
                  message={
                    <FormattedMessage
                      id="component:prompt.click-or-drop-upload-image"
                      defaultMessage="Click or drag and drop to upload image"
                    />
                  }
                />
              </div>
            )}
          </FileSelector>
        </div>
      )}
    </>
  );
}
